import { Configuration, PopupRequest } from '@azure/msal-browser';

const isLocalDevelopment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const getLocalOrigin = (): string => {
  const origin = window.location.origin;
  if (origin.includes('localhost') && origin.startsWith('https://')) {
    return origin.replace('https://', 'http://');
  }
  return origin;
};

// Prefer the current origin when running locally so MSAL auth completes on localhost,
// not on the production URL from .env (which causes redirects to the live site).
const getRedirectUri = (): string => {
  if (isLocalDevelopment()) {
    const localUri = getLocalOrigin();
    console.log('Using redirect URI for local development:', localUri);
    return localUri;
  }

  const envUri = process.env.REACT_APP_REDIRECT_URI;
  if (envUri) {
    console.log('Using redirect URI from environment:', envUri);
    return envUri;
  }

  const origin = getLocalOrigin();
  console.log('Using redirect URI from window.location.origin:', origin);
  return origin;
};

const getPostLogoutRedirectUri = (): string => {
  if (isLocalDevelopment()) {
    return getLocalOrigin();
  }

  return process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI || getRedirectUri();
};

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: process.env.REACT_APP_AZURE_AUTHORITY || 'https://login.microsoftonline.com/common',
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            return;
          case 1: // LogLevel.Warning
            console.warn(message);
            return;
          case 2: // LogLevel.Info
            console.info(message);
            return;
          case 3: // LogLevel.Verbose
            console.debug(message);
            return;
        }
      },
    },
  },
};

// Add scopes here for ID token to be used at Microsoft identity platform endpoints.
export const loginRequest: PopupRequest = {
  scopes: [
    'User.Read',
    'Sites.ReadWrite.All',
    'Files.ReadWrite.All',
    'Sites.FullControl.All'
  ],
  prompt: 'select_account',
};

// SharePoint-specific scopes for REST API access
export const sharePointRequest: PopupRequest = {
  scopes: [
    `${process.env.REACT_APP_SHAREPOINT_SITE_URL}/.default`
  ],
  prompt: 'select_account',
};

// Add the endpoints here for Microsoft Graph API services you'd like to use.
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
};
